import React, { useState } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';

// A board of boxes (groups) with cards (items) that the user drags from one box to another: the
// mapping of PMS segments to the user's own groups. Each card is in one box only.
// A box shows its title, the number of its cards, an optional gray second line and optional actions
// (rename, delete). The box with the tone 'attention' (amber-50, for example "Not grouped") shows
// the cards that still need a place.
// While a card moves, the box under the pointer gets a blue ground, and the card follows the pointer
// with a shadow. A pointer must move 6 px before a drag starts, so a click stays a click. The keyboard
// works too: Space or Enter picks a card up, the arrow keys move it, Space drops it, Escape cancels.
// The board keeps no state: onMove tells the caller the card and the new box, and the caller gives the
// new groups.
// 1 column on a phone, 2 from the sm width, 4 from the lg width.

export type GroupBoardItem = {
	id: string,
	label: string,
	// A gray second line, for example "1 hotel · 2,212 RN".
	meta?: string,
};

export type GroupBoardGroup = {
	id: string,
	title: string,
	// A gray second line, for example "4,343 RN in the imported stays".
	subtitle?: string,
	items: GroupBoardItem[],
	// 'attention': an amber-50 box for the cards that still need a place.
	tone?: 'default' | 'attention',
	// Buttons in the header of the box (rename, delete).
	actions?: React.ReactNode,
};

type Props = {
	groups: GroupBoardGroup[],
	onMove: (itemId: string, toGroupId: string) => void,
	// The accessible name of the board ("Groups of PMS segments").
	ariaLabel: string,
	// The text of an empty box. Default 'Drag items here'.
	emptyLabel?: string,
};

const Card: React.FunctionComponent<{ item: GroupBoardItem, lifted?: boolean, ghost?: boolean, handle?: React.ReactNode }> = ({ item, lifted = false, ghost = false, handle }) =>
	<div className={Utilities.classNames(
		'flex items-center gap-x-2 rounded-md bg-white px-2.5 py-2',
		lifted ? 'rotate-2 shadow-xl ring-2 ring-blue-600' : 'shadow-sm ring-1 ring-inset ring-gray-200',
		ghost ? 'opacity-40' : '',
	)}>
		{handle ?? <EllipsisVerticalIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />}
		<div className="min-w-0 flex-1">
			<p className="text-sm font-medium text-gray-900">{item.label}</p>
			{item.meta !== undefined && <p className="text-xs text-gray-500">{item.meta}</p>}
		</div>
	</div>;

const DraggableCard: React.FunctionComponent<{ item: GroupBoardItem, dragging: boolean }> = ({ item, dragging }) => {
	const { attributes, listeners, setNodeRef } = useDraggable({ id: item.id });
	return <div ref={setNodeRef} {...listeners} {...attributes} aria-roledescription="draggable card"
		className="cursor-grab rounded-md focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
		<Card item={item} ghost={dragging} />
	</div>;
};

const Box: React.FunctionComponent<{ group: GroupBoardGroup, activeId: string | null, fromId: string | null, emptyLabel: string }> = ({ group, activeId, fromId, emptyLabel }) => {
	const { isOver, setNodeRef } = useDroppable({ id: group.id });
	const target = isOver && activeId !== null && fromId !== group.id;
	return <section ref={setNodeRef} aria-label={group.title}
		className={Utilities.classNames('min-w-0 rounded-lg p-2.5 ring-1 ring-inset',
			target ? 'bg-blue-50 ring-blue-300' : (group.tone === 'attention' ? 'bg-amber-50 ring-amber-200' : 'bg-gray-50 ring-gray-200'))}>
		<div className="mb-2 flex items-start justify-between gap-x-2">
			<div className="min-w-0">
				<h3 className="text-sm font-semibold text-gray-900">{group.title} <span className="font-normal text-gray-400">· {group.items.length}</span></h3>
				{group.subtitle !== undefined && <p className="text-xs text-gray-500">{group.subtitle}</p>}
			</div>
			{group.actions !== undefined && <div className="flex shrink-0 items-center gap-x-1">{group.actions}</div>}
		</div>
		<div className="flex flex-col gap-y-1.5">
			{group.items.map(item => <DraggableCard key={item.id} item={item} dragging={item.id === activeId} />)}
			{target && <div className="flex h-10 items-center justify-center rounded-md border-2 border-dashed border-blue-200 text-xs font-medium text-blue-700">Drop here</div>}
			{group.items.length === 0 && !target && <p className="px-1 py-3 text-center text-xs text-gray-400">{emptyLabel}</p>}
		</div>
	</section>;
};

const GroupBoard: React.FunctionComponent<Props> = ({ groups, onMove, ariaLabel, emptyLabel = 'Drag items here' }) => {
	const [activeId, setActiveId] = useState<string | null>(null);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
	const groupOf = (itemId: string | null) => itemId === null ? null : (groups.find(g => g.items.some(i => i.id === itemId))?.id ?? null);
	const activeItem = activeId === null ? null : groups.flatMap(g => g.items).find(i => i.id === activeId) ?? null;

	const onDragStart = (event: DragStartEvent) => setActiveId(String(event.active.id));
	const onDragEnd = (event: DragEndEvent) => {
		const itemId = String(event.active.id);
		const to = event.over === null ? null : String(event.over.id);
		setActiveId(null);
		if (to !== null && to !== groupOf(itemId)) onMove(itemId, to);
	};

	return <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}
		accessibility={{ screenReaderInstructions: { draggable: 'Press Space or Enter to pick up the card. Use the arrow keys to move it to another group. Press Space to drop it, or Escape to cancel.' } }}>
		<div role="group" aria-label={ariaLabel} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
			{groups.map(group => <Box key={group.id} group={group} activeId={activeId} fromId={groupOf(activeId)} emptyLabel={emptyLabel} />)}
		</div>
		<DragOverlay>{activeItem !== null ? <Card item={activeItem} lifted /> : null}</DragOverlay>
	</DndContext>;
};

export default GroupBoard;
